import { useState } from 'react';
import { usePolling } from './usePolling';

/**
 * useCredentialsValidation - Hook for validating external service credentials
 * @param {string} csrfToken - CSRF token for API calls
 * @returns {Object} Validation state and functions
 */
export const useCredentialsValidation = (csrfToken) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [validationSuccess, setValidationSuccess] = useState(null);
  const [coursesData, setCoursesData] = useState(null);
  const [courseStructure, setCourseStructure] = useState(null);
  const [courseGroups, setCourseGroups] = useState(null);

  const { poll } = usePolling();

  /**
   * Validate credentials for an external service
   * @param {Object} credentials - The credentials to validate
   * @param {string} serviceName - The service name (duolingo, khan_academy, etc.)
   * @returns {Promise<Object>} Validation result
   */
  const validateCredentials = async (credentials, serviceName) => {
    setIsValidating(true);
    setValidationError(null);
    setValidationSuccess(null);
    setCoursesData(null);
    setCourseStructure(null);
    setCourseGroups(null);

    try {
      // Step 1: Create the async query
      let url = `/api/services?name=${serviceName}`;

      if (serviceName === 'khan_academy') {
        url += '&action=validate_credentials';
      }

      // Add credentials as query parameters
      Object.entries(credentials).forEach(([key, value]) => {
        url += `&details[${key}]=${encodeURIComponent(value)}`;
      });

      const createResponse = await fetch(url, {
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || 'Failed to create query');
      }

      const createData = await createResponse.json();
      const queryId = createData.query_id;

      // Step 2: Poll for the result
      const result = await poll(queryId, csrfToken);

      if (result.success) {
        setValidationSuccess('Credentials validated successfully!');
        setCoursesData(result.courses || []);
        setCourseStructure(result.course_structure || {});
        setCourseGroups(result.course_groups || null);
        setIsValidating(false);
        return {
          success: true,
          courses: result.courses || [],
          courseStructure: result.course_structure || {},
          courseGroups: result.course_groups || null
        };
      } else {
        setValidationError(result.error || 'Validation failed');
        setIsValidating(false);
        return { success: false, error: result.error };
      }
    } catch (error) {
      setValidationError(error.message);
      setIsValidating(false);
      return { success: false, error: error.message };
    }
  };

  const clearValidationState = () => {
    setValidationError(null);
    setValidationSuccess(null);
  };

  return {
    isValidating,
    validationError,
    validationSuccess,
    coursesData,
    courseStructure,
    courseGroups,
    validateCredentials,
    clearValidationState
  };
};
